import { Metric, Provider, ProviderScore, ProviderSovereigntyResult, RadarData, SOV_DOMAINS, HardRequirement } from '../types';

export const clamp = (n: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, n));
};

export const computeScores = (
  metrics: Metric[],
  providers: Provider[],
  weights: Record<string, number>,
  currentCloudUsage?: Record<string, boolean>,
  cloudActRelevant?: boolean
): ProviderScore[] => {
  const maxPerMetric = 10;
  return providers
    .map((p) => {
      let weighted = 0;
      let denom = 0;

      metrics.forEach((m) => {
        const w = clamp(weights[m.id] ?? m.weightDefault ?? 3, 1, 5);
        const s = p.scores[m.id] ?? 0;
        weighted += s * w;
        denom += w * maxPerMetric;
      });

      // Basis-Score 0–100
      let score = denom ? Math.round((weighted / denom) * 100) : 0;

      // ➕ Bonus: +8 Punkte für Provider, die aktuell genutzt werden
      if (currentCloudUsage && currentCloudUsage[p.id]) {
        score = Math.min(100, score + 8);
      }

      // Cloud Act disqualification: zero out score for subject providers
      if (cloudActRelevant && p.subjectToCloudAct) {
        score = 0;
      }

      return { id: p.id, name: p.name, score };
    })
    .sort((a, b) => b.score - a.score);
};

export const groupByCategory = (metrics: Metric[]): Record<string, Metric[]> => {
  return metrics.reduce<Record<string, Metric[]>>((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m);
    return acc;
  }, {});
};

export const buildHeatmap = (
  metrics: Metric[],
  providers: Provider[],
  weights: Record<string, number>
): Record<string, number | string>[] => {
  const maxPerMetric = 10;
  return providers.map((p) => {
    const row: Record<string, number | string> = { provider: p.name };
    metrics.forEach((m) => {
      const s = p.scores[m.id] ?? 0;
      const w = clamp(weights[m.id] ?? m.weightDefault ?? 3, 1, 5);
      const contribution = Math.round(((s / maxPerMetric) * (w / 5)) * 100);
      row[m.id] = contribution;
    });
    return row;
  });
};

export const buildRadar = (
  metrics: Metric[],
  providers: Provider[],
  weights: Record<string, number>,
  topProviders = 3,
  metricIds?: string[]
): RadarData => {
  const maxPerMetric = 10;

  let chosen: Metric[];
  if (metricIds && metricIds.length > 0) {
    const byId = new Map(metrics.map(m => [m.id, m]));
    chosen = metricIds.map(id => byId.get(id)).filter(Boolean) as Metric[];
  } else {
    chosen = [...metrics]
      .sort((a, b) => (weights[b.id] ?? b.weightDefault ?? 3) - (weights[a.id] ?? a.weightDefault ?? 3))
      .slice(0, 6);
  }

  const rankedProviders = [...providers]
    .map((p) => {
      let sum = 0, den = 0;
      metrics.forEach((m) => {
        const w = clamp(weights[m.id] ?? m.weightDefault ?? 3, 1, 5);
        const s = p.scores[m.id] ?? 0;
        sum += s * w;
        den += w * maxPerMetric;
      });
      const score = den ? sum / den : 0;
      return { provider: p, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, topProviders)
    .map((x) => x.provider);

  const radarData = chosen.map((m) => {
    const obj: any = { metric: m.label };
    rankedProviders.forEach((p) => {
      const s = p.scores[m.id] ?? 0;
      obj[p.name] = Math.round((s / maxPerMetric) * 100);
    });
    return obj;
  });

  return { radarData, rankedProviders, sortedMetrics: chosen };
};

export const buildQuestionOrder = (metrics: Metric[]): Metric[] => {
  const order = { Technical: 1, Regulatory: 2, Business: 3 } as Record<string, number>;
  return [...metrics].sort((a, b) => {
    const ca = order[a.category] ?? 99;
    const cb = order[b.category] ?? 99;
    if (ca !== cb) return ca - cb;
    return a.label.localeCompare(b.label);
  });
};

/**
 * Group metrics by their sovDomain field.
 * Metrics with sovDomain === null are placed in the "NONE" group.
 */
export function groupBySovDomain(metrics: Metric[]): Record<string, Metric[]> {
  return metrics.reduce<Record<string, Metric[]>>((acc, m) => {
    const key = m.sovDomain ?? 'NONE';
    (acc[key] = acc[key] || []).push(m);
    return acc;
  }, {});
}

/**
 * Compute domain score for a single provider and domain.
 * Formula: Σ(MetricScore × UserWeight) / Σ(UserWeight), result 0–10.
 * Missing scores (undefined) are treated as 0.
 * If no metrics have non-zero weight, returns 0.
 */
export function computeDomainScore(
  domainMetrics: Metric[],
  providerScores: Record<string, number | undefined>,
  weights: Record<string, number>
): number {
  let weightedSum = 0;
  let totalWeight = 0;

  domainMetrics.forEach((m) => {
    const w = clamp(weights[m.id] ?? m.weightDefault ?? 3, 1, 5);
    const s = providerScores[m.id] ?? 0;
    weightedSum += s * w;
    totalWeight += w;
  });

  if (totalWeight === 0) return 0;
  return weightedSum / totalWeight;
}

/**
 * Compute all domain scores (SOV-1 through SOV-8) for a provider.
 * Returns a Record mapping each SOV domain to its score (0–10).
 */
export function computeAllDomainScores(
  metrics: Metric[],
  provider: Provider,
  weights: Record<string, number>
): Record<string, number> {
  const grouped = groupBySovDomain(metrics);
  const result: Record<string, number> = {};

  for (const domain of SOV_DOMAINS) {
    const domainMetrics = grouped[domain] || [];
    result[domain] = computeDomainScore(domainMetrics, provider.scores, weights);
  }

  return result;
}

/**
 * Compute sovereignty score from domain scores and profile weights.
 * Formula: Σ(DomainScore(SOV-n) × DomainWeight(SOV-n)) for n=1..8
 * Result: 0–10
 */
export function computeSovereigntyScore(
  domainScores: Record<string, number>,
  profileWeights: Record<string, number>
): number {
  let score = 0;
  for (const domain of SOV_DOMAINS) {
    const ds = domainScores[domain] ?? 0;
    const pw = profileWeights[domain] ?? 0;
    score += ds * pw;
  }
  return score;
}

/**
 * Compute business score for metrics with sovDomain === null.
 * Formula: Σ(MetricScore × UserWeight) / Σ(UserWeight), result 0–10.
 * Uses computeDomainScore internally on the filtered null-domain metrics.
 */
export function computeBusinessScore(
  metrics: Metric[],
  provider: Provider,
  weights: Record<string, number>
): number {
  const businessMetrics = metrics.filter((m) => m.sovDomain === null);
  if (businessMetrics.length === 0) return 0;
  return computeDomainScore(businessMetrics, provider.scores, weights);
}

/**
 * Map a score (0–10) to SEAL level (0–4).
 * Thresholds: 0–2.99→0, 3.0–4.99→1, 5.0–6.99→2, 7.0–8.99→3, 9.0–10→4
 */
export function scoreToSealLevel(score: number): number {
  if (score >= 9.0) return 4;
  if (score >= 7.0) return 3;
  if (score >= 5.0) return 2;
  if (score >= 3.0) return 1;
  return 0;
}

/**
 * Check hard requirements for a provider.
 * Returns whether the provider passes all requirements and a list of failure labels.
 */
export function checkHardRequirements(
  provider: Provider,
  requirements: HardRequirement[],
  weights: Record<string, number>
): { passes: boolean; failures: string[] } {
  const failures: string[] = [];
  for (const req of requirements) {
    const score = provider.scores[req.metric] ?? 0;
    if (score < req.minScore) {
      failures.push(req.label);
    }
  }
  return { passes: failures.length === 0, failures };
}

/**
 * Compute simple score: weighted average of ALL metrics where sovDomain !== null.
 * Uses user weights. Result: 0–10.
 */
export function computeSimpleScore(
  metrics: Metric[],
  provider: Provider,
  weights: Record<string, number>
): number {
  const sovMetrics = metrics.filter((m) => m.sovDomain !== null);
  if (sovMetrics.length === 0) return 0;
  return computeDomainScore(sovMetrics, provider.scores, weights);
}

/**
 * Full sovereignty computation for all providers.
 * Pipeline per provider:
 *   1. Compute domain scores (SOV-1..SOV-8)
 *   2. Compute sovereignty score using profile weights
 *   3. Compute SEAL levels per domain and overall
 *   4. Compute business score (sovDomain === null metrics)
 *   5. Convert sovereignty score to percentage (0–100)
 *   6. Apply cloud usage bonus (+8, capped at 100)
 *   7. Apply Cloud Act disqualification (set to 0 if applicable)
 *   8. Apply framework mode logic (hard requirements, simple scoring, SOV-7 filter)
 *
 * Returns results sorted descending by sovereigntyScorePercent.
 */
export function computeSovereigntyResults(
  metrics: Metric[],
  providers: Provider[],
  weights: Record<string, number>,
  profileWeights: Record<string, number>,
  currentCloudUsage?: Record<string, boolean>,
  cloudActRelevant?: boolean,
  frameworkMode?: string,
  hardRequirements?: HardRequirement[]
): ProviderSovereigntyResult[] {
  const results: ProviderSovereigntyResult[] = providers.map((provider) => {
    // 1. Compute domain scores
    const domainScores = computeAllDomainScores(metrics, provider, weights);

    // 2. Compute sovereignty score based on framework mode
    let sovereigntyScore: number;
    if (frameworkMode === 'NO_FRAMEWORK_C5') {
      // Simple average of all SOV metrics (no domain weights)
      sovereigntyScore = computeSimpleScore(metrics, provider, weights);
    } else {
      // Weighted scoring using profile domain weights
      sovereigntyScore = computeSovereigntyScore(domainScores, profileWeights);
    }

    // 3. Compute SEAL levels per domain and overall
    const sealLevels: Record<string, number> = {};
    for (const domain of SOV_DOMAINS) {
      sealLevels[domain] = scoreToSealLevel(domainScores[domain] ?? 0);
    }
    const overallSealLevel = scoreToSealLevel(sovereigntyScore);

    // 4. Compute business score
    const businessScore = computeBusinessScore(metrics, provider, weights);

    // 5. Convert sovereignty score (0–10) to percentage (0–100)
    let sovereigntyScorePercent = (sovereigntyScore / 10) * 100;

    // 6. Apply cloud usage bonus
    if (currentCloudUsage && currentCloudUsage[provider.id]) {
      sovereigntyScorePercent = Math.min(100, sovereigntyScorePercent + 8);
    }

    // 7. Apply Cloud Act disqualification
    let disqualified = false;
    if (cloudActRelevant && provider.subjectToCloudAct) {
      sovereigntyScorePercent = 0;
      disqualified = true;
    }

    // 8. Framework mode specific logic
    let hardRequirementFailures: string[] | undefined;

    if (frameworkMode === 'NO_FRAMEWORK_C5') {
      // Filter: SOV-7 domain score must be >= 7
      const sov7Score = domainScores['SOV-7'] ?? 0;
      if (sov7Score < 7 && !disqualified) {
        sovereigntyScorePercent = 0;
        disqualified = true;
        hardRequirementFailures = ['SOV-7 Security < 7'];
      }
    } else if (frameworkMode === 'C3A' && hardRequirements && hardRequirements.length > 0) {
      // Check hard requirements
      const hrResult = checkHardRequirements(provider, hardRequirements, weights);
      if (!hrResult.passes) {
        hardRequirementFailures = hrResult.failures;
        if (!disqualified) {
          sovereigntyScorePercent = 0;
          disqualified = true;
        }
      }
    }

    return {
      id: provider.id,
      name: provider.name,
      sovereigntyScore,
      sovereigntyScorePercent,
      domainScores,
      sealLevels,
      overallSealLevel,
      businessScore,
      disqualified,
      hardRequirementFailures,
    };
  });

  // Sort descending by sovereigntyScorePercent
  results.sort((a, b) => b.sovereigntyScorePercent - a.sovereigntyScorePercent);

  return results;
}

/**
 * Build radar chart data using SOV domain axes.
 * Takes the top N providers (default 3) from results and creates
 * radar data with 8 data points (one per SOV domain).
 * Each data point has `metric: "SOV-1"` etc. and provider name → domain score.
 */
export function buildDomainRadar(
  results: ProviderSovereigntyResult[],
  topProviders: number = 3
): { radarData: any[]; rankedProviders: { id: string; name: string }[] } {
  const top = results.slice(0, topProviders);

  const rankedProviders = top.map((r) => ({ id: r.id, name: r.name }));

  const radarData = SOV_DOMAINS.map((domain) => {
    const point: any = { metric: domain };
    top.forEach((r) => {
      point[r.name] = r.domainScores[domain] ?? 0;
    });
    return point;
  });

  return { radarData, rankedProviders };
}
